import { logger } from '../services/logger';
import React, { useState, useEffect } from 'react';
import { UserBet, Market } from '../types';
import { BetTransactionService } from '../services/BetTransactionService';
import { ArrowUpRight, Info } from 'lucide-react';
import { useSettings } from '../lib/settings/settingsContext';
import { MarketEngine } from '../lib/marketEngine';
import { toast } from 'react-hot-toast';
import { ConfirmationModal } from './common/ConfirmationModal';
import { formatDateWithTimezone } from '../utils/dateUtils';

interface GroupedBet {
  market_option_id: number;
  option_name: string | undefined;
  total_amount: number;
  count: number;
  status: 'active' | 'won' | 'lost' | 'cancelled';
  last_bet_date: string | undefined;
  potential_payout: number;
  bets: UserBet[];
}

interface GroupedBetHistoryProps {
  refreshTrigger?: number;
  marketId: string;
  showTitle?: boolean;
}

function individualTradeAmounts(bet: UserBet, isLmsr: boolean) {
  const gross = Number(bet.amount ?? 0);
  const svc = Number(bet.service_fee_amount ?? 0);
  const aft = Number(bet.amount_after_fee ?? 0);
  const spreadFull = Number(bet.spread_revenue ?? 0);
  const initialGross =
    typeof bet.lmsr_initial_stake_gross === 'number' && bet.lmsr_initial_stake_gross > 0
      ? bet.lmsr_initial_stake_gross
      : gross;
  const spreadPart =
    isLmsr && spreadFull > 0 && initialGross > 0
      ? spreadFull * (gross / initialGross)
      : 0;
  const feesAndSpread = isLmsr ? svc + spreadPart : svc || Math.max(0, gross - aft);
  const net = isLmsr ? Math.max(0, gross - feesAndSpread) : aft;
  return { gross, feesAndSpread, net };
}

export function GroupedBetHistory({ 
  refreshTrigger = 0, 
  marketId, 
  showTitle = true
}: GroupedBetHistoryProps) {
  const [groupedBets, setGroupedBets] = useState<GroupedBet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOptionId, setExpandedOptionId] = useState<number | null>(null);
  const [cancelingBetId, setCancelingBetId] = useState<string | null>(null);
  const [market, setMarket] = useState<Market | null>(null);
  const { formatCurrency } = useSettings();
  const marketEngine = new MarketEngine();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [betToConfirm, setBetToConfirm] = useState<UserBet | null>(null);

  useEffect(() => {
    const fetchAndGroupBets = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user bets and market data
        const userBets = await BetTransactionService.getUserBetsForMarket(marketId);
        const fetchedMarket = await marketEngine.getMarket(marketId);
        
        if (!fetchedMarket) {
          throw new Error('Market data not found for grouping trades.');
        }
        setMarket(fetchedMarket);

        // Group bets by market_option_id
        const groupedMap = new Map<number, GroupedBet>();
        const marketOptionsMap = new Map(fetchedMarket.market_options.map(opt => [opt.id, opt]));

        userBets.forEach(bet => {
          const optionId = bet.market_option_id;
          const optionDetails = marketOptionsMap.get(optionId);

          if (!groupedMap.has(optionId)) {
            groupedMap.set(optionId, {
              market_option_id: optionId,
              option_name: optionDetails?.name,
              total_amount: 0,
              count: 0,
              status: 'active',
              last_bet_date: bet.time || bet.created_at || new Date().toISOString(),
              potential_payout: 0,
              bets: []
            });
          }
          
          const group = groupedMap.get(optionId)!;
          
          // Only count active bets in total amounts
          if (bet.status === 'active') {
            group.total_amount += bet.amount_after_fee;
            group.count += 1;
          }
          
          group.bets.push(bet);
          
          // Update last bet date if this bet is newer
          const betDate = new Date(bet.time || bet.created_at || new Date().toISOString());
          const groupDate = new Date(group.last_bet_date || new Date().toISOString());
          if (betDate > groupDate) {
            group.last_bet_date = bet.time || bet.created_at || new Date().toISOString();
          }
        });
        
        const isLmsr = fetchedMarket.engine_type === 'lmsr';

        // Calculate potential payouts for each outcome using the market data
        for (const group of groupedMap.values()) {
          const option = fetchedMarket.market_options.find(o => o.id === group.market_option_id);

          if (isLmsr) {
            const activePayout = group.bets
              .filter((b) => b.status === 'active')
              .reduce((sum, b) => sum + Number(b.shares_owned ?? 0), 0);
            group.potential_payout = activePayout;
            group.bets.forEach((bet) => {
              if (bet.status === 'active' && bet.shares_owned != null) {
                bet.potential_payout = Number(bet.shares_owned);
              }
            });
          } else if (option && option.odds !== undefined) {
            const potentialPayout = group.total_amount * option.odds;
            group.potential_payout = potentialPayout;

            group.bets.forEach((bet) => {
              if (!bet.potential_payout && bet.status === 'active' && option.odds !== undefined) {
                bet.potential_payout = bet.amount_after_fee * option.odds;
              }
            });
          }

          if (group.bets.length > 0) {
            const allBetsCancelled = group.bets.every((bet) => bet.status === 'cancelled');
            if (allBetsCancelled) {
              group.status = 'cancelled';
            }
          }
        }
        
        setGroupedBets(Array.from(groupedMap.values()));
      } catch (err) {
        logger.error('Error fetching and grouping bets:', err);
        const message = err instanceof Error ? err.message : 'Failed to load your trades';
        setError(message);
        setMarket(null);
        setGroupedBets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAndGroupBets();
  }, [refreshTrigger, marketId]);

  const toggleOutcomeExpansion = (optionId: number) => {
    if (expandedOptionId === optionId) {
      setExpandedOptionId(null);
    } else {
      setExpandedOptionId(optionId);
    }
  };

  // Cancel trade functionality disabled for users
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const requestCancelBet = (bet: UserBet) => {
    if (!canCancelBet(bet)) {
      toast.error("Cannot cancel this trade as trading has ended.");
      return;
    }
    setBetToConfirm(bet);
    setIsConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!betToConfirm) return;

    const betId = betToConfirm.id;
    setCancelingBetId(betId);
    setIsConfirmOpen(false);

    try {
      const result = await BetTransactionService.cancelBet(betId);
      
      if (result) { 
        const updatedGroups = [...groupedBets];
        let betFoundAndUpdated = false;
        for (const group of updatedGroups) {
          const betIndex = group.bets.findIndex(b => b.id === betId);
          if (betIndex !== -1) {
            const cancelledBet = { ...group.bets[betIndex], status: 'cancelled' as const };
            group.bets[betIndex] = cancelledBet;

            group.total_amount -= cancelledBet.amount_after_fee;
            group.count -= 1;
            if (cancelledBet.potential_payout) {
              group.potential_payout -= cancelledBet.potential_payout;
            }
            if (group.bets.every(b => b.status === 'cancelled')) {
              group.status = 'cancelled';
            }
            betFoundAndUpdated = true;
            break;
          }
        }

        if(betFoundAndUpdated) {
            setGroupedBets(updatedGroups);
        }
        
        toast.success('Trade cancelled successfully');
        window.dispatchEvent(new CustomEvent('betHistoryUpdated'));
        window.dispatchEvent(new CustomEvent('walletBalanceUpdated'));
      }
    } catch (error) {
      logger.error('Error cancelling bet:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel trade');
    } finally {
      setCancelingBetId(null);
      setBetToConfirm(null);
    }
  };

  const canCancelBet = (bet: UserBet): boolean => {
    if (!market) return false;

    const now = new Date();

    if (market.status !== 'active') return false;
    if (market.resolution_time && new Date(market.resolution_time) <= now) return false;

    const option = market.market_options.find(o => o.id === bet.market_option_id);
    if (!option) return false;

    if (market.market_type === 'fixed') {
      return !market.trading_end_time || new Date(market.trading_end_time) > now;
    } else if (market.market_type === 'knockout') {
      const optionEndTimeValid = !option.trading_end_time || new Date(option.trading_end_time) > now;
      const marketEndTimeValid = !market.trading_end_time || new Date(market.trading_end_time) > now;
      return optionEndTimeValid && marketEndTimeValid;
    } 
    
    return false; 
  };

  if (loading && groupedBets.length === 0) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-20 bg-gray-100 rounded mb-4"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
        <button 
          className="mt-2 text-sm text-red-600 hover:text-red-800" 
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (groupedBets.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-md">
        <p className="text-gray-600">You haven't placed any trades on this market yet.</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    if (status === 'won') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'lost') return 'bg-red-100 text-red-700 border-red-200';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-blue-100 text-blue-600 border-blue-200';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "No date";
    return formatDateWithTimezone(dateString, true);
  };

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Your Trades on This Market</h2>
          <div className="flex items-center text-xs text-gray-500">
            <Info className="w-3 h-3 mr-1" />
            <span>
              {market?.engine_type === 'lmsr'
                ? 'Payout equals your shares (R1 per share if this outcome wins).'
                : 'Potential payout is estimated from the pool and current odds.'}
            </span>
          </div>
        </div>
      )}
      
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outcome
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Net Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trades
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payout
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Trade
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupedBets.map((group) => (
                <React.Fragment key={group.market_option_id}>
                  <tr 
                    className={`hover:bg-gray-50 cursor-pointer ${expandedOptionId === group.market_option_id ? 'bg-gray-50' : ''}`}
                    onClick={() => toggleOutcomeExpansion(group.market_option_id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{group.option_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{formatCurrency(group.total_amount)}</div>
                      <div className="text-xs text-gray-500">After fees</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{group.count}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900 font-medium text-green-700">
                        {formatCurrency(group.potential_payout)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(group.status)}`}>
                        {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(group.last_bet_date || "No date")}</div>
                    </td>
                  </tr>
                  
                  {expandedOptionId === group.market_option_id && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <div className="bg-gray-50 p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Individual Trades</h4>
                          <div className="space-y-2">
                            {group.bets.map((bet) => {
                              const { gross, feesAndSpread, net } = individualTradeAmounts(
                                bet,
                                market?.engine_type === 'lmsr',
                              );
                              return (
                              <div key={bet.id} className="bg-white p-3 rounded border border-gray-200 shadow-sm flex justify-between items-center">
                                <div>
                                  <div className="space-y-0.5 text-sm text-gray-900">
                                    <div className="flex justify-between gap-6 max-w-md">
                                      <span className="text-gray-600">Gross amount</span>
                                      <span className="font-medium tabular-nums">{formatCurrency(gross)}</span>
                                    </div>
                                    <div className="flex justify-between gap-6 max-w-md">
                                      <span className="text-gray-600">Fees &amp; spread</span>
                                      <span className="font-medium tabular-nums">{formatCurrency(feesAndSpread)}</span>
                                    </div>
                                    <div className="flex justify-between gap-6 max-w-md font-medium">
                                      <span>Net trade</span>
                                      <span className="tabular-nums">{formatCurrency(net)}</span>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1.5">
                                    {formatDate(bet.time || bet.created_at || "Today")}
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <span className={`px-2 py-1 mr-2 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(bet.status)}`}>
                                    {bet.status.charAt(0).toUpperCase() + bet.status.slice(1)}
                                  </span>
                                  
                                  {bet.potential_payout != null &&
                                    bet.status === 'active' &&
                                    Number(bet.potential_payout) > 0 && (
                                    <div className="text-sm text-green-700 font-medium mr-3">
                                      {market?.engine_type === 'lmsr' ? 'Payout' : 'Potential'}:{' '}
                                      {formatCurrency(bet.potential_payout)}
                                    </div>
                                  )}
                                  {/* Cancel trade option hidden for users */}
                                  {/* {bet.status === 'active' && canCancelBet(bet) && (
                                    <div className="flex flex-col items-end">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          requestCancelBet(bet);
                                        }}
                                        disabled={cancelingBetId === bet.id}
                                        className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded-md border border-red-200 hover:bg-red-100 mr-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {cancelingBetId === bet.id ? (
                                          <span>Cancelling...</span>
                                        ) : (
                                          <>
                                            <XCircle className="w-3 h-3 mr-1" />
                                            <span>Cancel</span>
                                          </>
                                        )}
                                      </button>
                                      <div className="text-xs text-gray-500 mt-1 mr-2">
                                        Refund: {formatCurrency(bet.amount_after_fee)}
                                      </div>
                                    </div>
                                  )} */}
                                  <ArrowUpRight className="w-4 h-4 text-gray-400" />
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Confirm Trade Cancellation"
        message={
          betToConfirm ? (
            <div className="space-y-1 text-sm">
              <p>Are you sure you want to cancel your trade?</p>
              <p><strong>Market:</strong> {market?.title}</p>
              <p><strong>Outcome:</strong> {groupedBets.find(g => g.market_option_id === betToConfirm.market_option_id)?.option_name}</p>
              <p><strong>Trade Amount:</strong> {formatCurrency(betToConfirm.amount)}</p>
              <p><strong>Refund Amount (Net):</strong> {formatCurrency(betToConfirm.amount_after_fee)}</p>
            </div>
          ) : (
            'Loading trade details...'
          )
        }
        confirmText="Cancel Trade"
        cancelText="Keep Trade"
        isLoading={cancelingBetId === betToConfirm?.id}
        variant="danger"
      />
    </div>
  );
} 